import * as React from 'react';

import {PencilOutlineIcon} from 'shells/icon/svg/pencil-outline-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {
    getInvoiceActionMenuOptions,
    getQuoteActionMenuOptions,
} from 'shells/lead-sidebar/sales-documents/helpers';
import type {SalesDocumentType} from 'shells/lead-sidebar/sales-documents/types';
import {BaseRow} from 'shells/row/base-row/base-row';
import {Body} from 'shells/typography/body';

import type {QuoteDisplayStatus} from 'nutshell-graphql-types';
import type {
    GetQuotes_quotes as Quote,
    GetInvoices_invoices as Invoice,
} from 'nutshell-graphql-types';

import {getIdFromApiId} from '../../../../../core/utils/get-id-from-api-id';
import {appHistory} from '../../../../history';

import {getSalesDocumentStatusBadgeProps} from '../../sidebar/revenue-widget/components/helpers';

type Props = {
    quoteProps: {
        quotes: Quote[];
        handleDownloadQuote: (slug: string) => void;
        handleUpdateQuoteStatus: (id: string, status: QuoteDisplayStatus) => void;
        handleDuplicateQuote: (id: string) => void;
        handleSendQuoteViaEmail: () => void;
        handleCreateInvoiceFromQuote: (id: string) => void;
    };
    invoiceProps: {
        invoices: Invoice[];
        handleDownloadInvoice: (arg1: string) => void;
        handleUpdateInvoiceStatus: (id: string, status: QuoteDisplayStatus) => void;
        handleDuplicateInvoice: (id: string) => void;
        handleSendInvoiceViaEmail: () => void;
    };
};

export function SalesDocumentsRowList(props: Props) {
    const handleOpenSalesDocument = (type: SalesDocumentType, id: string) => {
        const numericId = getIdFromApiId(id);
        let hashBase;
        if (type === 'Quote') {
            hashBase = 'quotes';
        } else {
            hashBase = 'invoices';
        }
        appHistory.replace({
            ...appHistory.location,
            hash: `/${hashBase}/${numericId}`,
            state: {modal: true},
        });
    };

    return (
        <StackLegacy spacing={8}>
            {props.invoiceProps.invoices.map((invoice) => (
                <BaseRow
                    key={invoice.id}
                    type='invoices'
                    title={`Invoice ${invoice.displayNumber}`}
                    statusBadges={[
                        getSalesDocumentStatusBadgeProps(
                            invoice,
                            'Invoice',
                            invoice.displayStatus,
                            invoice.overdueDuration
                        ),
                    ]}
                    onClick={() => handleOpenSalesDocument('Invoice', invoice.id)}
                    toolbarProps={{
                        options: [
                            {
                                id: 'edit',
                                icon: PencilOutlineIcon,
                                tooltipText: 'Edit invoice',
                                onClick: () => handleOpenSalesDocument('Invoice', invoice.id),
                            },
                        ],
                        actionMenuOptions: {
                            options: getInvoiceActionMenuOptions(
                                invoice.id,
                                invoice.shareableViewInBrowserLink,
                                invoice.slug,
                                invoice.displayStatus,
                                props.invoiceProps.handleDownloadInvoice,
                                props.invoiceProps.handleDuplicateInvoice,
                                props.invoiceProps.handleUpdateInvoiceStatus,
                                props.invoiceProps.handleSendInvoiceViaEmail
                            ),
                        },
                    }}
                >
                    <Body size='small'>{invoice.total.formattedWithCents}</Body>
                </BaseRow>
            ))}
            {props.quoteProps.quotes.map((quote) => (
                <BaseRow
                    key={quote.id}
                    type='quotes'
                    title={`Quote ${quote.displayNumber}`}
                    statusBadges={[
                        getSalesDocumentStatusBadgeProps(quote, 'Quote', quote.displayStatus),
                    ]}
                    onClick={() => handleOpenSalesDocument('Quote', quote.id)}
                    toolbarProps={{
                        options: [
                            {
                                id: 'edit',
                                icon: PencilOutlineIcon,
                                tooltipText: 'Edit quote',
                                onClick: () => handleOpenSalesDocument('Quote', quote.id),
                            },
                        ],
                        actionMenuOptions: {
                            options: getQuoteActionMenuOptions(
                                quote.id,
                                quote.shareableViewInBrowserLink,
                                quote.slug,
                                quote.displayStatus,
                                props.quoteProps.handleDownloadQuote,
                                props.quoteProps.handleDuplicateQuote,
                                props.quoteProps.handleUpdateQuoteStatus,
                                props.quoteProps.handleSendQuoteViaEmail,
                                props.quoteProps.handleCreateInvoiceFromQuote
                            ),
                        },
                    }}
                >
                    <Body size='small'>{quote.total.formattedWithCents}</Body>
                </BaseRow>
            ))}
        </StackLegacy>
    );
}
